// components/rapport/reportspages/players/PlayerPositionReportPdf.jsx
import React from "react";
import { Page, Text, View, Document, StyleSheet, Image } from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page:{ padding:28, fontSize:11, fontFamily:"Helvetica" },
  h1:{ fontSize:18, fontWeight:700, marginBottom:2 },
  h2:{ fontSize:13, fontWeight:700, marginBottom:6 },
  small:{ fontSize:9, color:"#6b7280", marginTop:4 },

  // Cards & charts
  card:{ padding:10, borderWidth:1, borderColor:"#e5e7eb", borderRadius:10, marginBottom:12, backgroundColor:"#fff" },
  chartFull:{ width:"100%", objectFit:"contain", borderRadius:10, marginTop:6, marginBottom:8 },
  chartTall:{ width:"100%", height:520, objectFit:"contain", borderRadius:10, marginTop:6, marginBottom:8 },

  // Legend
  legendCard:{ padding:8, borderWidth:1, borderColor:"#e5e7eb", borderRadius:8, marginBottom:10, backgroundColor:"#fff" },
  legendItem:{ flexDirection:"row", alignItems:"center", marginBottom:3 },
  swatch:{ width:9, height:9, borderRadius:2, marginRight:6 },
  legendText:{ fontSize:8.5, color:"#374151" },

  // Table / Heatmap
  tableWrap:{ marginTop:4 },
  row:{ flexDirection:"row", alignItems:"stretch" },

  th:{ fontSize:8, fontWeight:700, paddingVertical:4, paddingHorizontal:4, borderBottomWidth:1, borderBottomColor:"#e5e7eb" },
  thTitle:{ lineHeight:1.15, textAlign:"right" },

  // Keep header simple; data rows very compact
  tdBase:{ borderBottomWidth:1, borderBottomColor:"#f1f5f9" },

  // Player cell (single line + badge)
  cellName:{ paddingVertical:4, paddingRight:6, paddingLeft:4, flexDirection:"row", alignItems:"center", gap:6 },
  playerName:{ fontSize:8.5 },
  posBadge:{ fontSize:7.5, color:"#6b7280" },

  // Metric cell: no extra padding here—color block will fill
  cellMetric:{ },

  // Colored block fills whole cell area
  cellBox:{
    minHeight:18,
    borderRadius:4,
    paddingHorizontal:4,
    paddingVertical:2,
    justifyContent:"center",
  },

  // Numbers inside colored block
  valueTxt:{ fontSize:8, fontWeight:600, textAlign:"right" },
  tiny:{ fontSize:7, color:"#6b7280", textAlign:"right", marginTop:1 },

  // Mini-bar stuff (kept for your comparison table below)
  inlineRight:{ flexDirection:"row", alignItems:"center", justifyContent:"flex-end", gap:6 },
  deltaUp:{ fontSize:7,color:"#059669" },
  deltaDown:{ fontSize:7,color:"#dc2626" },
  deltaFlat:{ fontSize:7,color:"#6b7280" },
  barWrap:{ width:"100%", height:10, backgroundColor:"#e5e7eb", borderRadius:5, position:"relative" },
  band:{ position:"absolute", top:0, bottom:0, backgroundColor:"rgba(14,165,233,0.18)", borderRadius:3 },
  tick:{ position:"absolute", top:0, bottom:0, width:1, backgroundColor:"rgba(59,130,246,0.85)" },
  dot:{ position:"absolute", width:6, height:6, borderRadius:3, top:2, marginLeft:-3 },
  dotIn:{ backgroundColor:"#10b981" },
  dotLow:{ backgroundColor:"#f59e0b" },
  dotHigh:{ backgroundColor:"#ef4444" },
});

const normalize = (s) => {
   if (!s) return null;
   // Accepte data:, http(s):, et blob:
   if (/^(data:|https?:|blob:)/.test(s)) return s;
   // Fallback si on te passe du base64 nu
   return `data:image/png;base64,${s}`;
 };

export default function PlayersPositionReportPdf({ season, images = {}, data = {} }) {
  const legend = data.__legend || {};
  const colorEntries = Object.entries(legend.colorMap || {}); // { position: color }
  const heatmap = data["players.heatmap_table"];

  const activeView = data.__activeView?.view || "box";
  // Ne garder que les images correspondant à la vue affichée
  const imageKeys = Object.keys(images).filter((k) => {
    if (!k.startsWith("players.")) return false;
    if (activeView === "heatmap") {
      // Heatmap: une seule capture
      return k === "players.heatmap";
    }
    // Box & Beeswarm: "players.box.<metric>" ou "players.beeswarm.<metric>"
    return k.startsWith(`players.${activeView}.`);
  });
  const tableData = data["players.table"];

  const fmt = (v, isPct) => {
    if (v == null || !isFinite(v)) return "—";
    return isPct ? `${(v*100).toFixed(1)}%` : v.toFixed(2);
  };
  const fmtDelta = (d, isPct) => {
    if (d == null || !isFinite(d)) return "";
    const sign = d > 0 ? "▲ " : d < 0 ? "▼ " : "· ";
    const abs = Math.abs(d);
    return sign + (isPct ? `${(abs*100).toFixed(1)}%` : abs.toFixed(2));
  };
  const fmtZ = (z) => (z == null || !isFinite(z) ? "" : `z ${z.toFixed(2)}`);

  // mini-viz calculs en px (bar 120px, bande entre 10% et 90%)
  const LEFT = 0.10; const RIGHT = 0.90;
  const posPctFromVal = (v, p10, p90) => {
    if (!isFinite(v) || !isFinite(p10) || !isFinite(p90) || p90 <= p10) return null;
    const t = (v - p10) / (p90 - p10);       // peut sortir de 0..1
    const rel = t * (RIGHT - LEFT) + LEFT;   // position relative 0..1 dans la cellule
    return Math.max(-0.02, Math.min(1.02, rel)) * 100; // %
  };

  const dotClass = (v, p10, p90) => {
    if (!isFinite(v) || !isFinite(p10) || !isFinite(p90) || p90 <= p10) return styles.dot;
    if (v < p10) return [styles.dot, styles.dotLow];
    if (v > p90) return [styles.dot, styles.dotHigh];
    return [styles.dot, styles.dotIn];
  };
   const NAME_PCT = 24; // doit matcher styles.cellName.width
   const metricPct = tableData?.columns?.length
     ? (100 - NAME_PCT) / tableData.columns.length
     : 0;

  const zColorPdf = (z) => {
  if (!Number.isFinite(z)) return "transparent";
  const t = Math.max(-2.5, Math.min(2.5, z)) / 2.5; // -1..1
  const r = t < 0 ? 255 : Math.round(220 * (1 - t));
  const b = t > 0 ? 255 : Math.round(220 * (1 + t));
  const g = 235 - Math.round(Math.abs(t) * 180);
  return `rgb(${r},${g},${b})`;
};
const FmtVal = (v, isPct) => (v == null || !isFinite(v)) ? "—" : (isPct ? `${(v*100).toFixed(1)}%` : v.toFixed(2));

function PdfHeatmapTable({ heatmap }) {
  // Tighter name column keeps table narrow
  const NAME_PCT = 22;
  const metricPct = heatmap?.columns?.length ? (100 - NAME_PCT) / heatmap.columns.length : 0;

  return (
    <View style={styles.card}>
      <Text style={styles.h2}>Heatmap — z-score vs poste</Text>

      <View style={styles.tableWrap}>
        {/* Header */}
        <View style={styles.row}>
          <View style={[styles.th, styles.tdBase, { width:`${NAME_PCT}%`, flexGrow:0, flexShrink:0 }]}>
            <Text>Player</Text>
          </View>
          {heatmap.columns.map(col => (
            <View
              key={col.key}
              style={[styles.th, styles.tdBase, { width:`${metricPct}%`, flexGrow:0, flexShrink:0 }]}
            >
              <Text style={styles.thTitle} wrap={false}>{col.label}</Text>
            </View>
          ))}
        </View>

        {/* Rows (allow page breaks if large) */}
        {heatmap.rows.map(r => (
          <View key={r.id} style={styles.row}>
            {/* Name cell: single line + small badge */}
            <View style={[styles.tdBase, styles.cellName, { width:`${NAME_PCT}%`, flexGrow:0, flexShrink:0 }]}>
              <Text style={styles.playerName} wrap={false}>{r.name}</Text>
              <Text style={styles.posBadge} wrap={false}>({r.position})</Text>
            </View>

            {/* Metric cells: colored box fills the cell area */}
            {r.cells.map(c => {
              const col = heatmap.columns.find(cc => cc.key === c.key) || { isPct:false };
              const bg = zColorPdf(c.z);
              return (
                <View
                  key={c.key}
                  style={[styles.tdBase, styles.cellMetric, { width:`${metricPct}%`, flexGrow:0, flexShrink:0 }]}
                >
                  <View style={[styles.cellBox, { backgroundColor: bg }]}>
                    <Text style={styles.valueTxt} wrap={false}>{FmtVal(c.value, col.isPct)}</Text>
                    <Text style={styles.tiny} wrap={false}>{Number.isFinite(c.z) ? `z ${c.z.toFixed(2)}` : ""}</Text>
                  </View>
                </View>
              );
            })}
          </View>
        ))}
      </View>
    </View>
  );
}
  
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.h1}>Players / Positions Report</Text>
        <Text style={styles.small}>Season {season}</Text>

        

        {/* Graphiques capturés */}
        {imageKeys.map(k => {
          const isHeatmap = k === "players.heatmap";
          return (
            <View key={k} style={styles.card} wrap={false}>
              <Text style={styles.h2}>{k.replace(/^players\./,"").replace(/\./g," / ")}</Text>
              <Image src={normalize(images[k])} style={isHeatmap ? styles.chartTall : styles.chartFull} />
            </View>
          );
        })}

        {activeView === "heatmap" && heatmap?.rows?.length > 0 && (
          <PdfHeatmapTable heatmap={heatmap} />
        )}

        {/* Tableau comparatif (avec mini-viz) */}
        {tableData && tableData.rows && tableData.rows.length > 0 && (
          <View style={styles.card} wrap={false}>
            <Text style={styles.h2}>Players comparison</Text>
            <View style={styles.tableWrap}>
              {/* Header */}
              <View style={styles.row}>
                <Text style={[styles.th, styles.cellName]}>Player</Text>
                {tableData.columns.map(col => (
                  <Text
                    key={col.key}
                    style={[styles.th, styles.cellMetric, { width:`${metricPct}%`, flexGrow:0, flexShrink:0 }]}
                    maxLines={2}
                  >
                    <Text style={styles.thTitle}>{col.label}</Text>
                  </Text>
                ))}
              </View>
              {/* Rows */}
              {tableData.rows.map(r => (
                <View key={r.id} style={styles.row}>
                  {/* Col identité: avatar + nom (position) */}
                  <View style={[styles.td, styles.cellName]}>
                    <View style={{ flexDirection:"column" }}>
                      <Text>{r.name}</Text>
                      <Text style={styles.subtle}>({r.position})</Text>
                    </View>
                  </View>

                  {/* Colonnes métriques */}
                  {r.cells.map(c => {
                    const col = tableData.columns.find(cc => cc.key === c.key) || { isPct:false };
                    const dotPct = posPctFromVal(c.value, c.p10, c.p90);
                    const deltaTxt = fmtDelta(c.delta, col.isPct);
                    const deltaStyle = !deltaTxt
                      ? styles.deltaFlat
                      : (c.delta > 0 ? styles.deltaUp : (c.delta < 0 ? styles.deltaDown : styles.deltaFlat));
                    const leftPct = LEFT * 100;
                    const rightPct = RIGHT * 100;
                    return (
                      <View key={c.key} style={[styles.td, styles.cellMetric, { width:`${metricPct}%`, flexGrow:0, flexShrink:0 }]}>
                        <View style={styles.inlineRight}>
                          <Text style={styles.valueTxt}>{fmt(c.value, col.isPct)}</Text>
                          <Text style={deltaStyle}>{deltaTxt}</Text>
                        </View>
                        {/* mini-viz */}
                        <View style={styles.barWrap}>
                          {/* bande P10–P90 centrée */}
                          {isFinite(c.p10) && isFinite(c.p90) && c.p90 > c.p10 && (
                            <>
                              <View style={[styles.band, { left: `${leftPct}%`, width: `${rightPct - leftPct}%` }]} />
                              <View style={[styles.tick, { left: `${leftPct}%` }]} />
                              <View style={[styles.tick, { left: `${rightPct}%` }]} />
                            </>
                          )}
                          {/* point */}
                          {dotPct != null && (
                            <View style={[...dotClass(c.value, c.p10, c.p90), { left: `${dotPct}%` }]} />
                          )}
                        </View>
                        <Text style={[styles.tiny, { textAlign:"right", marginTop:2 }]}>{fmtZ(c.z)}</Text>
                      </View>
                    );
                  })}
                </View>
              ))}
            </View>
          </View>
        )}
      </Page>
    </Document>
  );
}
